<template>
  <div class="ui-menubar" :class="rootClasses">
    <div class="ui-menubar-content">
      <!-- Start slot -->
      <div v-if="$slots.start" class="ui-menubar-start">
        <slot name="start"></slot>
      </div>

      <!-- Main menu items -->
      <ul ref="menuRef" class="ui-menubar-root-list" role="menubar">
        <li
          v-for="(item, index) in model"
          :key="index"
          :class="getItemClass(item, index)"
          role="none"
          @mouseenter="onItemMouseEnter($event, item, index)"
          @mouseleave="onItemMouseLeave($event, item, index)"
        >
          <div
            v-if="item.visible !== false"
            ref="itemRefs"
            :class="getItemLinkClass(item)"
            :aria-haspopup="item.items ? 'true' : 'false'"
            :aria-expanded="isItemActive(index)"
            :aria-level="1"
            :tabindex="getItemTabIndex(item, index)"
            role="menuitem"
            @click="onItemClick($event, item, index)"
            @keydown="onItemKeyDown($event, item, index)"
          >
            <!-- Custom item template or default -->
            <slot name="item" :item="item" :props="getItemProps(item, index)" :hasSubmenu="!!(item.items && item.items.length)" :root="true">
              <!-- Default item content -->
              <span v-if="item.icon" :class="['ui-menuitem-icon', item.icon]"></span>
              <span class="ui-menuitem-text">{{ item.label }}</span>
              <span v-if="item.badge" class="ui-menuitem-badge">{{ item.badge }}</span>
              <span v-if="item.shortcut" class="ui-menuitem-shortcut">{{ item.shortcut }}</span>
              <span v-if="item.items && item.items.length" class="ui-submenu-icon">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4.5 2L9.5 7L4.5 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </slot>
          </div>

          <!-- Submenu -->
          <Transition name="ui-submenu" @enter="onSubmenuEnter" @leave="onSubmenuLeave">
            <ul
              v-if="item.items && item.items.length && isItemActive(index)"
              ref="submenuRefs"
              :class="getSubmenuClass(item)"
              role="menu"
              :aria-labelledby="`menuitem-${index}`"
            >
              <li
                v-for="(submenuItem, submenuIndex) in item.items"
                :key="submenuIndex"
                :class="getSubmenuItemClass(submenuItem)"
                role="none"
              >
                <div
                  v-if="submenuItem.visible !== false"
                  :class="getSubmenuItemLinkClass(submenuItem)"
                  :tabindex="submenuItem.disabled ? -1 : 0"
                  role="menuitem"
                  @click="onSubmenuItemClick($event, submenuItem)"
                  @keydown="onSubmenuItemKeyDown($event, submenuItem, submenuIndex)"
                >
                  <!-- Custom submenu item template or default -->
                  <slot name="item" :item="submenuItem" :props="getSubmenuItemProps(submenuItem)" :hasSubmenu="false" :root="false">
                    <!-- Default submenu item content -->
                    <span v-if="submenuItem.icon" :class="['ui-menuitem-icon', submenuItem.icon]"></span>
                    <span class="ui-menuitem-text">{{ submenuItem.label }}</span>
                    <span v-if="submenuItem.badge" class="ui-menuitem-badge">{{ submenuItem.badge }}</span>
                    <span v-if="submenuItem.shortcut" class="ui-menuitem-shortcut">{{ submenuItem.shortcut }}</span>
                  </slot>
                </div>
              </li>
            </ul>
          </Transition>
        </li>
      </ul>

      <!-- End slot -->
      <div v-if="$slots.end" class="ui-menubar-end">
        <slot name="end"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'

export default {
  name: 'UIMenubar',
  props: {
    model: {
      type: Array,
      default: () => []
    },
    exact: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    tabindex: {
      type: Number,
      default: 0
    },
    ariaLabelledby: {
      type: String,
      default: null
    },
    ariaLabel: {
      type: String,
      default: null
    }
  },
  emits: ['item-click', 'item-mouseenter', 'item-mouseleave'],
  setup(props, { emit }) {
    const menuRef = ref(null)
    const itemRefs = ref([])
    const submenuRefs = ref([])
    const activeItemIndex = ref(null)
    const focusedItemIndex = ref(-1)

    const rootClasses = computed(() => [
      'ui-menubar-root',
      {
        'ui-menubar-disabled': props.disabled
      }
    ])

    // Item state management
    const isItemActive = (index) => {
      return activeItemIndex.value === index
    }

    const getItemClass = (item, index) => [
      'ui-menuitem',
      'ui-menuitem-root',
      {
        'ui-menuitem-active': isItemActive(index),
        'ui-menuitem-disabled': item.disabled,
        'ui-menuitem-focused': focusedItemIndex.value === index
      }
    ]

    const getItemLinkClass = (item) => [
      'ui-menuitem-link',
      'ui-menuitem-link-root',
      {
        'ui-menuitem-link-disabled': item.disabled
      }
    ]

    const getSubmenuClass = (item) => [
      'ui-submenu-list',
      'ui-menubar-submenu'
    ]

    const getSubmenuItemClass = (submenuItem) => [
      'ui-menuitem',
      'ui-menuitem-submenu',
      {
        'ui-menuitem-disabled': submenuItem.disabled
      }
    ]

    const getSubmenuItemLinkClass = (submenuItem) => [
      'ui-menuitem-link',
      'ui-menuitem-link-submenu',
      {
        'ui-menuitem-link-disabled': submenuItem.disabled
      }
    ]

    const getItemTabIndex = (item, index) => {
      if (item.disabled) return -1
      if (focusedItemIndex.value === -1 && index === 0) return props.tabindex
      if (focusedItemIndex.value === index) return props.tabindex
      return -1
    }

    const getItemProps = (item, index) => ({
      action: {
        class: getItemLinkClass(item),
        onClick: (event) => onItemClick(event, item, index),
        onKeydown: (event) => onItemKeyDown(event, item, index)
      }
    })

    const getSubmenuItemProps = (submenuItem) => ({
      action: {
        class: getSubmenuItemLinkClass(submenuItem),
        onClick: (event) => onSubmenuItemClick(event, submenuItem),
        onKeydown: (event) => onSubmenuItemKeyDown(event, submenuItem)
      }
    })

    // Event handlers
    const onItemClick = (event, item, index) => {
      if (item.disabled) {
        event.preventDefault()
        return
      }

      if (item.command) {
        item.command({ originalEvent: event, item })
      }

      if (item.items && item.items.length) {
        if (activeItemIndex.value === index) {
          activeItemIndex.value = null
        } else {
          activeItemIndex.value = index
        }
        event.preventDefault()
      } else {
        // Navigate if it's a link
        if (item.to && (item.type === 'router' || !item.type)) {
          // Router navigation would be handled by the parent
          activeItemIndex.value = null
        } else if (item.to && item.type === 'external') {
          window.open(item.to, '_blank')
          activeItemIndex.value = null
        }
      }

      emit('item-click', { originalEvent: event, item, index })
    }

    const onSubmenuItemClick = (event, submenuItem) => {
      if (submenuItem.disabled) {
        event.preventDefault()
        return
      }

      if (submenuItem.command) {
        submenuItem.command({ originalEvent: event, item: submenuItem })
      }

      if (submenuItem.to && (submenuItem.type === 'router' || !submenuItem.type)) {
        // Router navigation would be handled by the parent
        activeItemIndex.value = null
      } else if (submenuItem.to && submenuItem.type === 'external') {
        window.open(submenuItem.to, '_blank')
        activeItemIndex.value = null
      }

      // Close submenu after clicking an item
      activeItemIndex.value = null

      emit('item-click', { originalEvent: event, item: submenuItem })
    }

    const onItemMouseEnter = (event, item, index) => {
      if (item.disabled) return

      // Close other submenus and open this one if it has items
      if (item.items && item.items.length) {
        activeItemIndex.value = index
      } else if (activeItemIndex.value !== null) {
        // Close active submenu if hovering over item without submenu
        activeItemIndex.value = null
      }

      emit('item-mouseenter', { originalEvent: event, item, index })
    }

    const onItemMouseLeave = (event, item, index) => {
      // Don't immediately close submenu on mouse leave
      // Let the submenu itself handle closing
      emit('item-mouseleave', { originalEvent: event, item, index })
    }

    const onItemKeyDown = (event, item, index) => {
      switch (event.code) {
        case 'ArrowDown':
          if (item.items && item.items.length) {
            activeItemIndex.value = index
            nextTick(() => {
              const submenuFirstItem = submenuRefs.value[0]?.querySelector('.ui-menuitem-link')
              if (submenuFirstItem) {
                submenuFirstItem.focus()
              }
            })
          }
          event.preventDefault()
          break

        case 'ArrowRight':
          focusedItemIndex.value = Math.min(focusedItemIndex.value + 1, props.model.length - 1)
          nextTick(() => {
            const nextItem = itemRefs.value[focusedItemIndex.value]
            if (nextItem) {
              nextItem.focus()
            }
          })
          event.preventDefault()
          break

        case 'ArrowLeft':
          focusedItemIndex.value = Math.max(focusedItemIndex.value - 1, 0)
          nextTick(() => {
            const prevItem = itemRefs.value[focusedItemIndex.value]
            if (prevItem) {
              prevItem.focus()
            }
          })
          event.preventDefault()
          break

        case 'Enter':
        case 'Space':
          onItemClick(event, item, index)
          break

        case 'Escape':
          activeItemIndex.value = null
          event.preventDefault()
          break
      }
    }

    const onSubmenuItemKeyDown = (event, submenuItem, submenuIndex) => {
      switch (event.code) {
        case 'ArrowUp':
          // Focus previous submenu item
          event.preventDefault()
          break

        case 'ArrowDown':
          // Focus next submenu item
          event.preventDefault()
          break

        case 'Enter':
        case 'Space':
          onSubmenuItemClick(event, submenuItem)
          break

        case 'Escape':
          activeItemIndex.value = null
          // Focus back to parent item
          nextTick(() => {
            const parentItem = itemRefs.value[activeItemIndex.value]
            if (parentItem) {
              parentItem.focus()
            }
          })
          event.preventDefault()
          break
      }
    }

    // Transition handlers
    const onSubmenuEnter = (el) => {
      el.style.height = '0px'
      el.style.opacity = '0'
      el.offsetHeight // Force reflow
      el.style.height = 'auto'
      el.style.opacity = '1'
    }

    const onSubmenuLeave = (el) => {
      el.style.height = el.scrollHeight + 'px'
      el.style.opacity = '1'
      el.offsetHeight // Force reflow
      el.style.height = '0px'
      el.style.opacity = '0'
    }

    // Outside click handler
    const onDocumentClick = (event) => {
      if (!menuRef.value?.contains(event.target)) {
        activeItemIndex.value = null
        focusedItemIndex.value = -1
      }
    }

    onMounted(() => {
      document.addEventListener('click', onDocumentClick)
    })

    onUnmounted(() => {
      document.removeEventListener('click', onDocumentClick)
    })

    return {
      menuRef,
      itemRefs,
      submenuRefs,
      activeItemIndex,
      focusedItemIndex,
      rootClasses,
      isItemActive,
      getItemClass,
      getItemLinkClass,
      getSubmenuClass,
      getSubmenuItemClass,
      getSubmenuItemLinkClass,
      getItemTabIndex,
      getItemProps,
      getSubmenuItemProps,
      onItemClick,
      onSubmenuItemClick,
      onItemMouseEnter,
      onItemMouseLeave,
      onItemKeyDown,
      onSubmenuItemKeyDown,
      onSubmenuEnter,
      onSubmenuLeave
    }
  }
}
</script>

<style scoped>
/* Menubar Root */
.ui-menubar-root {
  @apply bg-white shadow-sm relative;
  /* Force light theme */
  background-color: white !important;
  color: #374151 !important;
}

.ui-menubar-disabled {
  @apply opacity-60 pointer-events-none;
}

/* Menubar Content */
.ui-menubar-content {
  @apply flex items-center justify-between w-full;
}

.ui-menubar-start,
.ui-menubar-end {
  @apply flex items-center;
}

/* Root List */
.ui-menubar-root-list {
  @apply flex items-center list-none m-0 p-0;
}

/* Menu Items */
.ui-menuitem-root {
  @apply relative;
}

.ui-menuitem-link-root {
  @apply flex items-center px-4 py-3 text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors duration-150 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset;
  /* Force light theme */
  color: #374151 !important;
  background-color: transparent !important;
}

.ui-menuitem-link-root:hover {
  background-color: #f9fafb !important;
  color: #111827 !important;
}

.ui-menuitem-link-disabled {
  @apply opacity-60 cursor-not-allowed pointer-events-none;
}

.ui-menuitem-active .ui-menuitem-link-root {
  @apply bg-gray-100 text-gray-900;
}

.ui-menuitem-focused .ui-menuitem-link-root {
  @apply ring-2 ring-blue-500;
}

/* Menu Item Components */
.ui-menuitem-icon {
  @apply mr-2 w-4 h-4;
}

.ui-menuitem-text {
  @apply font-medium;
}

.ui-menuitem-badge {
  @apply ml-2 px-2 py-1 text-xs bg-red-500 text-white rounded-full;
}

.ui-menuitem-shortcut {
  @apply ml-auto text-xs text-gray-500 border border-gray-300 rounded px-1;
}

.ui-submenu-icon {
  @apply ml-2 text-gray-400;
}

/* Submenu */
.ui-menubar-submenu {
  @apply absolute left-0 top-full mt-1 min-w-48 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden;
  z-index: 110; /* Consistent with navigation z-index */
  /* Force light theme */
  background-color: white !important;
  color: #374151 !important;
}

.ui-menuitem-submenu {
  @apply border-b border-gray-100 last:border-b-0;
}

.ui-menuitem-link-submenu {
  @apply flex items-center px-4 py-3 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors duration-150 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset;
  /* Force light theme */
  color: #374151 !important;
}

.ui-menuitem-link-submenu:hover {
  background-color: #f9fafb !important;
  color: #111827 !important;
}

/* Transitions */
.ui-submenu-enter-active,
.ui-submenu-leave-active {
  transition: all 0.2s ease;
}

.ui-submenu-enter-from,
.ui-submenu-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Responsive */
@media (max-width: 767px) {
  .ui-menubar-root-list {
    @apply flex-col w-full;
  }
  
  .ui-menuitem-root {
    @apply w-full;
  }
  
  .ui-menubar-submenu {
    @apply static w-full mt-0 border-0 shadow-none bg-gray-50;
  }
}

/* iPad and Tablet specific (768px - 1024px) */
@media (min-width: 768px) and (max-width: 1024px) {
  .ui-menubar-content {
    @apply overflow-x-auto overflow-y-visible;
    -webkit-overflow-scrolling: touch;
  }
  
  .ui-menubar-root-list {
    @apply flex-nowrap;
    min-width: max-content;
    gap: 0.25rem;
  }
  
  .ui-menuitem-root {
    @apply flex-shrink-0;
    position: relative;
  }
  
  .ui-menuitem-link-root {
    @apply px-2 py-3 whitespace-nowrap text-sm;
  }
  
  .ui-menuitem-text {
    @apply text-sm;
  }
  
  .ui-menuitem-icon {
    @apply w-4 h-4 mr-1;
  }
  
  .ui-menubar-submenu {
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-top: 0.25rem;
    max-width: calc(100vw - 2rem);
    z-index: 110;
  }
  
  /* Ensure submenu doesn't go off-screen */
  .ui-menuitem-root:first-child .ui-menubar-submenu {
    left: 0;
    transform: none;
  }
  
  .ui-menuitem-root:last-child .ui-menubar-submenu {
    right: 0;
    left: auto;
    transform: none;
  }
}

/* Medium screens (1025px - 1280px) */
@media (min-width: 1025px) and (max-width: 1280px) {
  .ui-menubar-content {
    @apply overflow-x-auto overflow-y-visible;
    -webkit-overflow-scrolling: touch;
  }
  
  .ui-menubar-root-list {
    @apply flex-nowrap;
    min-width: max-content;
  }
  
  .ui-menuitem-root {
    @apply flex-shrink-0;
  }
  
  .ui-menuitem-link-root {
    @apply px-3 py-3 whitespace-nowrap text-sm;
  }
  
  .ui-menubar-submenu {
    @apply min-w-48;
    left: 0;
    right: auto;
  }
}

/* Prevent wrapping on desktop screens */
@media (min-width: 768px) {
  .ui-menubar-root-list {
    @apply flex-nowrap;
  }
  
  .ui-menuitem-root {
    @apply flex-shrink-0;
  }
  
  .ui-menuitem-link-root {
    @apply whitespace-nowrap;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .ui-menubar-root {
    @apply border-2 border-gray-800;
  }
  
  .ui-menubar-submenu {
    @apply border-2 border-gray-800;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .ui-menuitem-link-root,
  .ui-menuitem-link-submenu,
  .ui-submenu-enter-active,
  .ui-submenu-leave-active {
    transition: none;
  }
}

/* Dark mode support - Only activate when explicitly requested */
@media (prefers-color-scheme: dark) {
  /* Commented out automatic dark mode to prevent unwanted activation
  .ui-menubar-root {
    @apply bg-gray-800 border-gray-600;
  }
  
  .ui-menuitem-link-root {
    @apply text-gray-200 hover:text-white hover:bg-gray-700;
  }
  
  .ui-menubar-submenu {
    @apply bg-gray-800 border-gray-600;
  }
  
  .ui-menuitem-link-submenu {
    @apply text-gray-200 hover:text-white hover:bg-gray-700;
  }
  
  .ui-submenu-icon {
    @apply text-gray-400;
  }
  */
}
</style> 